import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import queryString from 'query-string';
import axios, { AxiosError, AxiosHeaders } from 'axios';
import { getAuthHeaders } from '../utils/auth.utils';
import { toast } from 'react-toastify';

type TokenStorageKeys = {
  accessToken: 'accessToken' | 'superAdminAccessToken';
};

const SUPER_ADMIN_URL_FRAGMENT = 'super-admin';

const isSuperAdminContext = () => (window.location.href || '').includes(SUPER_ADMIN_URL_FRAGMENT);

const getTokenStorageKeys = (isSuperAdmin: boolean): TokenStorageKeys => {
  if (isSuperAdmin) {
    return { accessToken: 'superAdminAccessToken' };
  }

  return { accessToken: 'accessToken' };
};

const getRefreshEndpoint = (isSuperAdmin: boolean) =>
  isSuperAdmin ? '/v1/admin/refresh-token' : '/v1/user/refresh-token';

export const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_CONVERSATION_API_URL,
  prepareHeaders: (headers) => {
    const isSuperAdmin = isSuperAdminContext();
    const { accessToken } = getTokenStorageKeys(isSuperAdmin);
    const token = localStorage.getItem(accessToken);
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }

    return headers;
  },
  paramsSerializer: (params) => {
    return queryString.stringify(params);
  },
  async responseHandler(response) {
    const data = await response.clone().json();
    const unauthorized = response.status === 401 || data.status === 401 || data.statusCode === 401;
    const isSuperAdmin = isSuperAdminContext();
    const tokenStorageKeys = getTokenStorageKeys(isSuperAdmin);

    if (data.status === 401 || data.statusCode === 401) {
      if (data && typeof data === 'object' && 'message' in data) {
        toast.error(data.message);
      } else {
        toast.error('Authentication failed');
      }
    }

    if (unauthorized) {
      try {
        // refreshToken lives in an HttpOnly cookie on core-api - the browser attaches it automatically
        const refreshResponse = await axios.post(
          `${import.meta.env.VITE_API_URL}${getRefreshEndpoint(isSuperAdmin)}`,
          {},
          { withCredentials: true },
        );

        const { accessToken } = refreshResponse.data;

        localStorage.setItem(tokenStorageKeys.accessToken, accessToken);

        const retryResponse = await fetch(response.url, {
          ...response,
          headers: getAuthHeaders(response.headers as unknown as AxiosHeaders, accessToken),
        });

        return await retryResponse.json();
      } catch (error: unknown) {
        const axiosError = error as AxiosError<{ message?: string }>;
        const errorData = axiosError.response?.data;

        if (errorData && typeof errorData === 'object' && 'message' in errorData) {
          toast.error(errorData.message);
        }

        return { error: { status: 401, data: 'Unauthorized-Token Expired' } };
      }
    }

    return data;
  },
});

export const baseApi = createApi({
  reducerPath: 'conversationApi',
  baseQuery,
  endpoints: () => ({}),
});
